import React, { useState, useRef, useEffect } from 'react';
import { getVolume, speakText, setReplay } from './lzAudio';

// Tap-to-fill coloring game with a color-mixing lab (ages 3-5).
// Pictures are data (IMAGES below): every region is one tappable SVG shape,
// so completion always counts exactly what is drawn.

const BASE = [
    { id: 'red', label: 'Red', hex: '#FF3B30' },
    { id: 'orange', label: 'Orange', hex: '#FF7F00' },
    { id: 'yellow', label: 'Yellow', hex: '#FFCC00' },
    { id: 'green', label: 'Green', hex: '#28A745' },
    { id: 'blue', label: 'Blue', hex: '#1A6EFF' },
    { id: 'indigo', label: 'Indigo', hex: '#3949AB' },
    { id: 'violet', label: 'Violet', hex: '#8B2BE2' },
    { id: 'white', label: 'White', hex: '#F8F8F8' },
    { id: 'black', label: 'Black', hex: '#1A1A1A' },
];

// [a, b, id, label, hex] - every one of the C(9,2)=36 pairs.
const MIX_LIST = [
    ['red', 'orange', 'coral', 'Coral', '#FF6B6B'], ['red', 'yellow', 'tomato', 'Tomato', '#FF4500'],
    ['red', 'green', 'brown', 'Brown', '#795548'], ['red', 'blue', 'rose', 'Rose', '#C0007A'],
    ['red', 'indigo', 'crimson', 'Crimson', '#9B0030'], ['red', 'violet', 'hotpink', 'Hot Pink', '#FF1493'],
    ['red', 'white', 'babypink', 'Baby Pink', '#FFB6C1'], ['red', 'black', 'maroon', 'Maroon', '#800000'],
    ['orange', 'yellow', 'amber', 'Amber', '#FFB300'], ['orange', 'green', 'moss', 'Moss', '#6B7A22'],
    ['orange', 'blue', 'rust', 'Rust', '#9A5020'], ['orange', 'indigo', 'terracotta', 'Terracotta', '#C0441C'],
    ['orange', 'violet', 'brick', 'Brick', '#B03020'], ['orange', 'white', 'peach', 'Peach', '#FFDAB9'],
    ['orange', 'black', 'copper', 'Copper', '#CC5500'], ['yellow', 'green', 'lime', 'Lime', '#7FB800'],
    ['yellow', 'blue', 'grass', 'Grass', '#80B000'], ['yellow', 'indigo', 'olive', 'Olive', '#808000'],
    ['yellow', 'violet', 'khaki', 'Khaki', '#9A9020'], ['yellow', 'white', 'cream', 'Cream', '#FFFDE7'],
    ['yellow', 'black', 'gold', 'Gold', '#B8A000'], ['green', 'blue', 'teal', 'Teal', '#00868B'],
    ['green', 'indigo', 'forest', 'Forest', '#1A5C40'], ['green', 'violet', 'sage', 'Sage', '#5A7050'],
    ['green', 'white', 'mint', 'Mint', '#90EE90'], ['green', 'black', 'darkgreen', 'Dark Green', '#1B5E20'],
    ['blue', 'indigo', 'periwinkle', 'Periwinkle', '#5C6BC0'], ['blue', 'violet', 'grape', 'Grape', '#6633AA'],
    ['blue', 'white', 'skyblue', 'Sky Blue', '#87CEEB'], ['blue', 'black', 'navy', 'Navy', '#003080'],
    ['indigo', 'violet', 'deeppurple', 'Deep Purple', '#5B2C8D'], ['indigo', 'white', 'lavender', 'Lavender', '#C5CAE9'],
    ['indigo', 'black', 'midnight', 'Midnight', '#191970'], ['violet', 'white', 'lilac', 'Lilac', '#DDA0DD'],
    ['violet', 'black', 'eggplant', 'Eggplant', '#4A148C'], ['white', 'black', 'silver', 'Silver', '#C0C0C0'],
];

const C = {};
BASE.forEach((b) => { C[b.id] = { ...b, parents: null }; });
const MIX = {};
MIX_LIST.forEach(([a, b, id, label, hex]) => {
    C[id] = { id, label, hex, parents: [a, b] };
    MIX[`${a}+${b}`] = id;
    MIX[`${b}+${a}`] = id;
});

const FREQ = { red: 380, orange: 420, yellow: 480, green: 440, blue: 360, indigo: 340, violet: 320, white: 560, black: 300 };
const freqOf = (id) => {
    const c = C[id];
    if (!c) return 400;
    return c.parents ? (FREQ[c.parents[0]] + FREQ[c.parents[1]]) / 2 : FREQ[id];
};

// Bright colors the 🌈 Magic tool picks from (no black/white - surprise
// should always look fun).
const MAGIC = ['red', 'orange', 'yellow', 'green', 'blue', 'violet', 'coral', 'hotpink', 'amber', 'lime', 'teal', 'skyblue', 'mint', 'lilac', 'peach', 'babypink', 'periwinkle', 'grape'];

const RECIPES = [
    ['red', 'yellow'], ['blue', 'yellow'], ['red', 'blue'], ['red', 'white'], ['blue', 'white'],
    ['green', 'blue'], ['orange', 'white'], ['violet', 'white'], ['white', 'black'],
];

const range = (n) => Array.from({ length: n }, (_, i) => i);

const arcPath = (o, i, cx = 150, cy = 235) => (i === 0
    ? `M ${cx - o},${cy} A ${o},${o} 0 0 1 ${cx + o},${cy} L ${cx},${cy} Z`
    : `M ${cx - o},${cy} A ${o},${o} 0 0 1 ${cx + o},${cy} L ${cx + i},${cy} A ${i},${i} 0 0 0 ${cx - i},${cy} Z`);

// Region: { id, el, a: attrs, rot?: wrapper transform, clip?: inside rainbow clip }.
// Several shapes may share an id (e.g. cloud puffs) - they fill together.
// `decor` is non-interactive detail drawn on top (eyes, smiles, lines).
const IMAGES = [
    {
        id: 'sun', label: 'Sun', icon: '☀️',
        regions: [
            { id: 'sky', el: 'rect', a: { x: 0, y: 0, width: 300, height: 300 } },
            { id: 'ground', el: 'rect', a: { x: 0, y: 235, width: 300, height: 65 } },
            ...range(8).map((n) => ({ id: `ray${n}`, el: 'rect', a: { x: 142, y: 22, width: 16, height: 52, rx: 8 }, rot: `rotate(${n * 45}, 150, 150)` })),
            { id: 'body', el: 'circle', a: { cx: 150, cy: 150, r: 68 } },
        ],
        decor: (
            <>
                <circle cx={125} cy={138} r={9} fill="#1A1A1A" /><circle cx={175} cy={138} r={9} fill="#1A1A1A" />
                <circle cx={128} cy={135} r={3} fill="#fff" /><circle cx={178} cy={135} r={3} fill="#fff" />
                <path d="M 118,168 Q 150,196 182,168" stroke="#1A1A1A" strokeWidth={4} fill="none" strokeLinecap="round" />
            </>
        ),
    },
    {
        id: 'flower', label: 'Flower', icon: '🌸',
        regions: [
            { id: 'sky', el: 'rect', a: { x: 0, y: 0, width: 300, height: 300 } },
            { id: 'ground', el: 'rect', a: { x: 0, y: 230, width: 300, height: 70 } },
            { id: 'stem', el: 'rect', a: { x: 144, y: 168, width: 12, height: 80, rx: 6 } },
            { id: 'leaf-l', el: 'ellipse', a: { cx: 105, cy: 218, rx: 36, ry: 18 }, rot: 'rotate(-30,105,218)' },
            { id: 'leaf-r', el: 'ellipse', a: { cx: 195, cy: 218, rx: 36, ry: 18 }, rot: 'rotate(30,195,218)' },
            ...range(6).map((n) => ({ id: `petal${n}`, el: 'ellipse', a: { cx: 150, cy: 88, rx: 22, ry: 38 }, rot: `rotate(${n * 60}, 150, 148)` })),
            { id: 'center', el: 'circle', a: { cx: 150, cy: 148, r: 32 } },
        ],
        decor: (
            <>
                <circle cx={139} cy={140} r={6} fill="#1A1A1A" /><circle cx={161} cy={140} r={6} fill="#1A1A1A" />
                <path d="M 136,156 Q 150,170 164,156" stroke="#1A1A1A" strokeWidth={3.5} fill="none" strokeLinecap="round" />
            </>
        ),
    },
    {
        id: 'rainbow', label: 'Rainbow', icon: '🌈', clip: true,
        regions: [
            { id: 'sky', el: 'rect', a: { x: 0, y: 0, width: 300, height: 300 } },
            { id: 'ground', el: 'rect', a: { x: 0, y: 240, width: 300, height: 60 } },
            { id: 'band-red', el: 'path', a: { d: arcPath(148, 118) }, clip: true },
            { id: 'band-orange', el: 'path', a: { d: arcPath(118, 88) }, clip: true },
            { id: 'band-yellow', el: 'path', a: { d: arcPath(88, 58) }, clip: true },
            { id: 'band-green', el: 'path', a: { d: arcPath(58, 28) }, clip: true },
            { id: 'band-blue', el: 'path', a: { d: arcPath(28, 0) }, clip: true },
            ...[[40, 235, 30, 22], [62, 220, 26, 20], [84, 229, 22, 17]].map(([cx, cy, rx, ry]) => ({ id: 'cl', el: 'ellipse', a: { cx, cy, rx, ry }, clip: true })),
            ...[[260, 235, 30, 22], [238, 220, 26, 20], [216, 229, 22, 17]].map(([cx, cy, rx, ry]) => ({ id: 'cr', el: 'ellipse', a: { cx, cy, rx, ry }, clip: true })),
        ],
    },
    {
        id: 'butterfly', label: 'Butterfly', icon: '🦋',
        regions: [
            { id: 'sky', el: 'rect', a: { x: 0, y: 0, width: 300, height: 300 } },
            { id: 'ground', el: 'rect', a: { x: 0, y: 240, width: 300, height: 60 } },
            { id: 'wing-ul', el: 'path', a: { d: 'M 148,145 C 118,95 48,65 28,118 C 10,162 78,192 148,168 Z' } },
            { id: 'wing-ur', el: 'path', a: { d: 'M 152,145 C 182,95 252,65 272,118 C 290,162 222,192 152,168 Z' } },
            { id: 'wing-ll', el: 'path', a: { d: 'M 148,172 C 118,182 65,215 78,255 C 90,278 138,268 148,222 Z' } },
            { id: 'wing-lr', el: 'path', a: { d: 'M 152,172 C 182,182 235,215 222,255 C 210,278 162,268 152,222 Z' } },
            { id: 'body', el: 'ellipse', a: { cx: 150, cy: 178, rx: 11, ry: 52 } },
        ],
        decor: (
            <>
                <g opacity={0.25} fill="#333">
                    <circle cx={82} cy={128} r={16} /><circle cx={218} cy={128} r={16} />
                    <circle cx={112} cy={228} r={11} /><circle cx={188} cy={228} r={11} />
                </g>
                <path d="M 146,130 Q 136,100 120,90" stroke="#333" strokeWidth={2.5} fill="none" />
                <path d="M 154,130 Q 164,100 180,90" stroke="#333" strokeWidth={2.5} fill="none" />
                <circle cx={120} cy={90} r={5} fill="#333" /><circle cx={180} cy={90} r={5} fill="#333" />
            </>
        ),
    },
    {
        id: 'fish', label: 'Fish', icon: '🐠',
        regions: [
            { id: 'water', el: 'rect', a: { x: 0, y: 0, width: 300, height: 300 } },
            { id: 'sand', el: 'rect', a: { x: 0, y: 245, width: 300, height: 55 } },
            { id: 'seaweed', el: 'path', a: { d: 'M 34,245 Q 18,205 40,175 Q 22,145 44,112 L 56,118 Q 36,146 54,174 Q 32,205 48,245 Z' } },
            { id: 'tail', el: 'polygon', a: { points: '210,150 272,104 272,196' } },
            { id: 'fin-top', el: 'path', a: { d: 'M 108,104 Q 142,52 180,104 Z' } },
            { id: 'fin-bottom', el: 'path', a: { d: 'M 118,196 Q 142,238 170,196 Z' } },
            { id: 'body', el: 'ellipse', a: { cx: 140, cy: 150, rx: 86, ry: 56 } },
            { id: 'stripe', el: 'ellipse', a: { cx: 158, cy: 150, rx: 13, ry: 50 } },
        ],
        decor: (
            <>
                <circle cx={96} cy={138} r={11} fill="#fff" stroke="#333" strokeWidth={2} /><circle cx={94} cy={138} r={6} fill="#1A1A1A" />
                <path d="M 72,166 Q 82,176 94,166" stroke="#1A1A1A" strokeWidth={3} fill="none" strokeLinecap="round" />
                <circle cx={60} cy={92} r={8} fill="none" stroke="#fff" strokeWidth={2.5} />
                <circle cx={48} cy={66} r={5} fill="none" stroke="#fff" strokeWidth={2.5} />
                <circle cx={56} cy={44} r={3.5} fill="none" stroke="#fff" strokeWidth={2.5} />
            </>
        ),
    },
    {
        id: 'house', label: 'House', icon: '🏠',
        regions: [
            { id: 'sky', el: 'rect', a: { x: 0, y: 0, width: 300, height: 300 } },
            { id: 'ground', el: 'rect', a: { x: 0, y: 238, width: 300, height: 62 } },
            { id: 'sun', el: 'circle', a: { cx: 252, cy: 48, r: 26 } },
            { id: 'chimney', el: 'rect', a: { x: 196, y: 62, width: 24, height: 56 } },
            { id: 'roof', el: 'polygon', a: { points: '150,48 42,140 258,140' } },
            { id: 'wall', el: 'rect', a: { x: 64, y: 140, width: 172, height: 100 } },
            { id: 'door', el: 'rect', a: { x: 130, y: 176, width: 40, height: 64, rx: 6 } },
            { id: 'window-l', el: 'rect', a: { x: 82, y: 160, width: 36, height: 36 } },
            { id: 'window-r', el: 'rect', a: { x: 182, y: 160, width: 36, height: 36 } },
        ],
        decor: (
            <>
                <line x1={100} y1={160} x2={100} y2={196} stroke="#333" strokeWidth={2.5} />
                <line x1={82} y1={178} x2={118} y2={178} stroke="#333" strokeWidth={2.5} />
                <line x1={200} y1={160} x2={200} y2={196} stroke="#333" strokeWidth={2.5} />
                <line x1={182} y1={178} x2={218} y2={178} stroke="#333" strokeWidth={2.5} />
                <circle cx={161} cy={210} r={4} fill="#333" />
            </>
        ),
    },
    {
        id: 'car', label: 'Car', icon: '🚗',
        regions: [
            { id: 'sky', el: 'rect', a: { x: 0, y: 0, width: 300, height: 300 } },
            { id: 'cloud', el: 'ellipse', a: { cx: 78, cy: 58, rx: 44, ry: 22 } },
            { id: 'road', el: 'rect', a: { x: 0, y: 222, width: 300, height: 78 } },
            { id: 'cabin', el: 'path', a: { d: 'M 92,150 L 118,102 L 200,102 L 228,150 Z' } },
            { id: 'body', el: 'rect', a: { x: 40, y: 146, width: 220, height: 64, rx: 22 } },
            { id: 'window-f', el: 'polygon', a: { points: '126,112 158,112 158,146 108,146' } },
            { id: 'window-b', el: 'polygon', a: { points: '166,112 196,112 216,146 166,146' } },
            { id: 'wheel-l', el: 'circle', a: { cx: 98, cy: 214, r: 28 } },
            { id: 'wheel-r', el: 'circle', a: { cx: 210, cy: 214, r: 28 } },
        ],
        decor: (
            <>
                <circle cx={98} cy={214} r={9} fill="#fff" stroke="#333" strokeWidth={2.5} />
                <circle cx={210} cy={214} r={9} fill="#fff" stroke="#333" strokeWidth={2.5} />
                <line x1={20} y1={262} x2={60} y2={262} stroke="#fff" strokeWidth={6} />
                <line x1={130} y1={262} x2={170} y2={262} stroke="#fff" strokeWidth={6} />
                <line x1={240} y1={262} x2={280} y2={262} stroke="#fff" strokeWidth={6} />
                <circle cx={250} cy={168} r={6} fill="#FFEB3B" stroke="#333" strokeWidth={2} />
            </>
        ),
    },
    {
        id: 'icecream', label: 'Ice Cream', icon: '🍦',
        regions: [
            { id: 'bg', el: 'rect', a: { x: 0, y: 0, width: 300, height: 300 } },
            { id: 'table', el: 'rect', a: { x: 0, y: 262, width: 300, height: 38 } },
            { id: 'cone', el: 'polygon', a: { points: '104,150 196,150 150,272' } },
            { id: 'scoop-bottom', el: 'circle', a: { cx: 150, cy: 132, r: 50 } },
            { id: 'scoop-top', el: 'circle', a: { cx: 150, cy: 80, r: 40 } },
            { id: 'cherry', el: 'circle', a: { cx: 150, cy: 36, r: 13 } },
        ],
        decor: (
            <>
                <path d="M 116,182 L 170,182 M 128,214 L 162,214 M 138,244 L 156,244" stroke="#333" strokeWidth={2} />
                <path d="M 154,24 Q 160,8 174,6" stroke="#333" strokeWidth={3} fill="none" strokeLinecap="round" />
                <g fill="#333" opacity={0.5}>
                    <rect x={124} y={120} width={9} height={3} rx={1.5} transform="rotate(30 128 121)" />
                    <rect x={164} y={140} width={9} height={3} rx={1.5} transform="rotate(-20 168 141)" />
                    <rect x={140} y={70} width={9} height={3} rx={1.5} transform="rotate(50 144 71)" />
                    <rect x={160} y={92} width={9} height={3} rx={1.5} transform="rotate(-40 164 93)" />
                </g>
            </>
        ),
    },
];

const REGION_IDS = Object.fromEntries(IMAGES.map((img) => [img.id, [...new Set(img.regions.map((r) => r.id))]]));
const TOTAL_REGIONS = Object.fromEntries(Object.entries(REGION_IDS).map(([k, v]) => [k, v.length]));
const EMPTY_COLORS = () => Object.fromEntries(IMAGES.map((img) => [img.id, {}]));

const PHRASES = ["You're an artist!", 'So colorful!', 'Amazing work!', 'Wonderful!', 'What a masterpiece!'];
const CONFETTI = ['#FF3B30', '#FFCC00', '#28A745', '#1A6EFF', '#8B2BE2', '#FF69B4', '#FF7F00'];

const ColoringGame = ({ childId, childAge, onColorFound, onImageComplete }) => {
    const storageKey = childId ? `syntch_coloring_discovered_${childId}` : 'syntch_coloring_discovered';

    const [currentImage, setCurrentImage] = useState('sun');
    const [regionColors, setRegionColors] = useState(EMPTY_COLORS);
    const [selectedColor, setSelectedColor] = useState('red');
    const [tool, setTool] = useState('paint'); // 'paint' | 'magic' | 'eraser'
    const [mixSlot1, setMixSlot1] = useState(null);
    const [mixSlot2, setMixSlot2] = useState(null);
    const [discovered, setDiscovered] = useState([]);
    const [newlyFound, setNewlyFound] = useState(null);
    const [celebrating, setCelebrating] = useState(false);
    const [finished, setFinished] = useState(() => new Set());
    const [phrase, setPhrase] = useState(PHRASES[0]);
    const [sparkles, setSparkles] = useState([]);

    const audioCtx = useRef(null);
    const svgRef = useRef(null);
    const canvasRef = useRef(null);
    const history = useRef([]); // [{ image, regionId, prev }]
    const timers = useRef([]);
    const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    useEffect(() => () => {
        timers.current.forEach(clearTimeout);
        try { window.speechSynthesis?.cancel(); } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            const list = raw ? JSON.parse(raw) : [];
            setDiscovered(Array.isArray(list) ? list.filter((id) => C[id]) : []);
        } catch (e) {
            setDiscovered([]);
        }
    }, [storageKey]);

    // ─── Sound ──────────────────────────────────────────────────────────────
    const tone = (freq, delayMs = 0, gain = 0.12, dur = 0.18) => {
        const vol = getVolume();
        if (!vol) return;
        try {
            if (!audioCtx.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return;
                audioCtx.current = new Ctx();
            }
            const ctx = audioCtx.current;
            if (ctx.state === 'suspended') ctx.resume();
            const t = ctx.currentTime + delayMs / 1000;
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            g.gain.setValueAtTime(gain * vol, t);
            g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
            osc.connect(g).connect(ctx.destination);
            osc.start(t);
            osc.stop(t + dur + 0.02);
        } catch (e) { /* never block on audio */ }
    };
    const playTone = (colorId) => tone(freqOf(colorId));
    const playFanfare = () => [523, 659, 784, 1047].forEach((f, i) => tone(f, i * 120, 0.16, 0.22));
    const say = (text) => speakText(text);

    const pickColor = (id) => {
        setSelectedColor(id);
        if (tool === 'eraser') setTool('paint');
        playTone(id);
        say(C[id].label);
    };

    // ─── Painting ───────────────────────────────────────────────────────────
    const image = IMAGES.find((i) => i.id === currentImage);
    const colors = regionColors[currentImage] || {};
    const filledCount = Object.keys(colors).length;
    const total = TOTAL_REGIONS[currentImage];

    const addSparkle = (e) => {
        const box = canvasRef.current?.getBoundingClientRect();
        if (!box) return;
        const id = `${Date.now()}-${Math.random()}`;
        setSparkles((s) => [...s, { id, x: e.clientX - box.left, y: e.clientY - box.top }]);
        later(() => setSparkles((s) => s.filter((p) => p.id !== id)), 700);
    };

    const fillRegion = (regionId, e) => {
        const prev = colors[regionId];
        let hex = null;
        let soundId = selectedColor;
        if (tool === 'magic') {
            soundId = MAGIC[Math.floor(Math.random() * MAGIC.length)];
            hex = C[soundId].hex;
        } else if (tool === 'paint') {
            hex = C[selectedColor].hex;
        }
        if (hex === (prev || null)) return;

        const nextMap = { ...colors };
        if (hex) nextMap[regionId] = hex; else delete nextMap[regionId];
        history.current.push({ image: currentImage, regionId, prev });
        setRegionColors((p) => ({ ...p, [currentImage]: nextMap }));
        if (e) addSparkle(e);
        if (hex) playTone(soundId); else tone(260, 0, 0.08, 0.12);

        if (hex && !finished.has(currentImage) && Object.keys(nextMap).length >= total) {
            const img = currentImage;
            setFinished((s) => new Set(s).add(img));
            later(() => {
                const p = PHRASES[Math.floor(Math.random() * PHRASES.length)];
                setPhrase(p);
                setCelebrating(true);
                playFanfare();
                say(`Beautiful! ${p}`);
                onImageComplete?.(img, nextMap);
                later(() => setCelebrating(false), 3000);
            }, 250);
        }
    };

    const undo = () => {
        const last = [...history.current].reverse().find((h) => h.image === currentImage);
        if (!last) return;
        history.current.splice(history.current.lastIndexOf(last), 1);
        setRegionColors((p) => {
            const map = { ...p[currentImage] };
            if (last.prev) map[last.regionId] = last.prev; else delete map[last.regionId];
            return { ...p, [currentImage]: map };
        });
        tone(330, 0, 0.08, 0.12);
    };

    const startOver = () => {
        history.current = history.current.filter((h) => h.image !== currentImage);
        setRegionColors((p) => ({ ...p, [currentImage]: {} }));
        setFinished((s) => { const n = new Set(s); n.delete(currentImage); return n; });
    };

    // Rasterizes the live SVG to a PNG download ("Save my picture").
    const savePicture = () => {
        try {
            const svg = svgRef.current;
            if (!svg) return;
            const xml = new XMLSerializer().serializeToString(svg);
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = 900;
                canvas.height = 900;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = '#fff';
                ctx.fillRect(0, 0, 900, 900);
                ctx.drawImage(img, 0, 0, 900, 900);
                const a = document.createElement('a');
                a.download = `my-${currentImage}-picture.png`;
                a.href = canvas.toDataURL('image/png');
                a.click();
            };
            img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
            say('Saved!');
        } catch (e) { /* ignore */ }
    };

    // ─── Mix lab ────────────────────────────────────────────────────────────
    const pickMixColor = (colorId) => {
        if (!mixSlot1) setMixSlot1(colorId);
        else if (!mixSlot2 && colorId !== mixSlot1) setMixSlot2(colorId);
        else if (colorId === mixSlot1) { setMixSlot1(mixSlot2); setMixSlot2(null); }
        else setMixSlot2(colorId);
        playTone(colorId);
    };

    const mixResult = mixSlot1 && mixSlot2 ? MIX[`${mixSlot1}+${mixSlot2}`] : null;

    useEffect(() => {
        if (mixResult) say(`${C[mixSlot1].label} and ${C[mixSlot2].label} make ${C[mixResult].label}!`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mixResult]);

    const applyMixedColor = () => {
        if (!mixResult) return;
        setSelectedColor(mixResult);
        setTool('paint');
        if (!discovered.includes(mixResult)) {
            const next = [...discovered, mixResult];
            setDiscovered(next);
            setNewlyFound(mixResult);
            later(() => setNewlyFound(null), 1600);
            try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch (e) { /* ignore */ }
            playFanfare();
            onColorFound?.(mixResult, C[mixResult].label, next.length);
        } else {
            playTone(mixResult);
        }
        setMixSlot1(null);
        setMixSlot2(null);
    };

    // ─── Render helpers ─────────────────────────────────────────────────────
    const renderRegion = (r, i) => {
        const fill = colors[r.id];
        const Tag = r.el;
        const el = (
            <Tag
                key={`${r.id}-${i}-${fill || 'none'}`}
                {...r.a}
                fill={fill || '#FFFFFF'}
                stroke="#333"
                strokeWidth={2.5}
                className={`region ${fill ? 'filled' : ''}`}
                onClick={(e) => fillRegion(r.id, e)}
            />
        );
        // Rotation lives on a wrapper so the fill animation (which sets
        // `transform`) can't clobber the shape's orientation.
        return r.rot ? <g key={`${r.id}-${i}`} transform={r.rot}>{el}</g> : el;
    };

    const swatchBorder = (id) => (id === 'white' ? '#CCC' : id === 'black' ? '#555' : 'rgba(0,0,0,.15)');

    const pot = (id, size, onClick, extraClass = '') => (
        <button
            key={id}
            type="button"
            title={C[id].label}
            aria-label={C[id].label}
            onClick={onClick}
            className={`cg-pot ${selectedColor === id && tool === 'paint' ? 'active' : ''} ${extraClass}`}
            style={{ width: size, height: size, background: C[id].hex, borderColor: swatchBorder(id) }}
        />
    );

    const slot = (id, onClick) => (
        <button
            type="button"
            onClick={onClick}
            aria-label={id ? `Clear ${C[id].label}` : 'Empty slot'}
            className={`cg-slot ${id ? 'full' : ''}`}
            style={id ? { background: C[id].hex } : undefined}
        >
            {!id && '?'}
        </button>
    );

    const pct = Math.round((filledCount / total) * 100);

    useEffect(() => {
        setReplay(tool === 'magic'
            ? `Magic colors! Tap any part of the ${image.label} for a surprise color.`
            : tool === 'eraser'
                ? `Eraser. Tap a part of the ${image.label} to clean it.`
                : `${C[selectedColor].label}. Tap a part of the ${image.label} to color it!`);
        return () => setReplay(null);
    }, [tool, selectedColor, image]);
    const activeHex = tool === 'eraser' ? '#FFFFFF' : tool === 'magic' ? null : C[selectedColor].hex;

    return (
        <div className="cg-root">
            <style>{`
                /* One-screen layout: the canvas is sized from the viewport
                   height (modal body is capped at 100vh - 170px), and the
                   tools / colors sit beside it instead of below, so the whole
                   game fits without scrolling on laptop/desktop screens. */
                .cg-root {
                    --cg-size: clamp(250px, calc(100vh - 366px), 520px);
                    position: relative; padding: 10px 12px; color: #3e2723; user-select: none;
                    font-family: 'Baloo 2', 'Fredoka One', 'Comic Sans MS', cursive;
                }
                .cg-top { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
                .cg-tabs { display: flex; gap: 6px; overflow-x: auto; padding: 8px 2px 6px; flex: 1; justify-content: center; }
                .cg-tab {
                    position: relative; flex-shrink: 0; display: flex; align-items: center; gap: 4px; height: 42px; padding: 0 12px;
                    border-radius: 21px; border: 3px solid #FFE0B2; background: #FFF8E1; cursor: pointer;
                    font-weight: 800; font-size: 13px; color: #6D4C41; transition: transform .15s; box-shadow: 0 3px 0 #FFE0B2; font-family: inherit;
                }
                .cg-tab span { font-size: 20px; }
                .cg-tab:hover { transform: translateY(-2px); }
                .cg-tab.active { border-color: #FF9800; background: #FFE0B2; box-shadow: 0 3px 0 #FF9800; }
                .cg-check { position: absolute; top: -9px; right: -6px; width: 20px; height: 20px; border-radius: 50%; background: #22C55E; color: #fff; font-size: 12px; font-style: normal; display: flex; align-items: center; justify-content: center; border: 2px solid #fff; }
                .cg-sound { flex-shrink: 0; width: 42px; height: 42px; border-radius: 50%; border: 3px solid #B3E5FC; background: #E1F5FE; font-size: 19px; cursor: pointer; }

                .cg-main { display: grid; grid-template-columns: 76px var(--cg-size) minmax(280px, 330px); gap: 16px; justify-content: center; align-items: start; }

                .cg-rail { display: flex; flex-direction: column; gap: 6px; }
                .cg-rail-label { font-size: 10px; line-height: 1; font-weight: 900; color: #8D6E63; text-align: center; }
                .cg-tool {
                    display: flex; flex-direction: column; align-items: center; gap: 0; padding: 3px 2px; border-radius: 14px; cursor: pointer;
                    border: 3px solid #E2E8F0; background: #fff; font-weight: 900; font-size: 11px; font-family: inherit; color: #3e2723;
                    box-shadow: 0 3px 0 #E2E8F0;
                }
                .cg-tool span { font-size: 22px; line-height: 1.15; }
                .cg-tool.active { border-color: #8B2BE2; background: #F3E5F5; box-shadow: 0 3px 0 #8B2BE2; }
                .cg-rail hr { width: 100%; border: none; border-top: 2px dashed #FFCC80; margin: 0; }
                .cg-act {
                    height: 40px; border-radius: 12px; border: none; font-weight: 900; font-size: 10px; cursor: pointer; font-family: inherit;
                    color: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; line-height: 1.1;
                }
                .cg-act b { font-size: 16px; }
                .cg-act:active { transform: translateY(2px); }
                .cg-act:disabled { opacity: .4; cursor: not-allowed; }
                .cg-act.undo { background: #64B5F6; box-shadow: 0 3px 0 #1E88E5; }
                .cg-act.reset { background: #FF8A65; box-shadow: 0 3px 0 #E64A19; }
                .cg-act.save { background: #66BB6A; box-shadow: 0 3px 0 #388E3C; }

                .cg-center { display: flex; flex-direction: column; gap: 8px; width: var(--cg-size); }
                .cg-frame {
                    position: relative; width: var(--cg-size); height: var(--cg-size); padding: 9px; box-sizing: border-box; border-radius: 24px;
                    background: repeating-linear-gradient(45deg, #FFCC80, #FFCC80 12px, #FFE0B2 12px, #FFE0B2 24px);
                    box-shadow: 0 6px 0 #E0A060, 0 12px 24px rgba(0,0,0,.16);
                }
                .cg-canvas { position: relative; width: 100%; height: 100%; border-radius: 16px; overflow: hidden; background: #fff; cursor: crosshair; }
                .cg-canvas svg { display: block; width: 100%; height: 100%; }
                .region { transform-box: fill-box; transform-origin: center; }
                .region:hover { opacity: .85; }
                .region.filled { animation: region-fill .3s ease; }
                .cg-sparkle { position: absolute; pointer-events: none; transform: translate(-50%, -50%); font-size: 26px; animation: cg-sparkle .7s ease-out forwards; }
                .cg-progress { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 900; color: #6D4C41; }
                .cg-track { flex: 1; height: 14px; border-radius: 8px; background: #FFE0B2; overflow: hidden; border: 2px solid #FFCC80; }
                .cg-fill { height: 100%; border-radius: 6px; background: linear-gradient(90deg, #FF3B30, #FF7F00, #FFCC00, #28A745, #1A6EFF, #8B2BE2); transition: width .35s ease; }

                .cg-panel { display: flex; flex-direction: column; gap: 8px; max-height: calc(var(--cg-size) + 30px); overflow-y: auto; padding: 2px; }
                .cg-card { background: #fff; border-radius: 18px; padding: 9px 12px; box-shadow: 0 3px 10px rgba(0,0,0,.07); border: 3px solid var(--edge, #F1F5F9); }
                .cg-title { font-weight: 900; font-size: 14px; margin-bottom: 6px; display: flex; align-items: center; justify-content: space-between; gap: 6px; }
                .cg-title small { font-size: 11px; font-weight: 800; color: #8D6E63; }
                .cg-current-dot { width: 26px; height: 26px; border-radius: 50%; border: 3px solid #333; flex-shrink: 0; }
                .cg-current-dot.magic { background: conic-gradient(#FF3B30, #FFCC00, #28A745, #1A6EFF, #8B2BE2, #FF3B30); animation: cg-spin 3s linear infinite; }
                .cg-pots { display: flex; flex-wrap: wrap; gap: 7px; }
                .cg-pot {
                    border-radius: 50%; border: 3px solid; cursor: pointer; padding: 0; flex-shrink: 0;
                    box-shadow: inset -4px -5px 0 rgba(0,0,0,.18), inset 4px 4px 0 rgba(255,255,255,.35); transition: transform .12s;
                }
                .cg-pot:hover { transform: scale(1.12); }
                .cg-pot.active { transform: scale(1.12); box-shadow: 0 0 0 3px #fff, 0 0 0 6px #3e2723, inset -4px -5px 0 rgba(0,0,0,.18); }
                .cg-pot.pop { animation: disc-pop .5s ease; }
                .cg-mine { display: flex; gap: 6px; overflow-x: auto; padding: 4px 2px; }
                .cg-empty { font-size: 12px; color: #8D6E63; font-weight: 700; }

                .cg-lab-row { display: flex; align-items: center; justify-content: center; gap: 6px; }
                .cg-op { font-weight: 900; font-size: 20px; color: #8D6E63; }
                .cg-slot { width: 44px; height: 44px; border-radius: 50%; border: 3px dashed #B0BEC5; background: #fff; cursor: pointer; font-weight: 900; font-size: 18px; color: #B0BEC5; font-family: inherit; }
                .cg-slot.full { border-style: solid; border-color: #3e2723; box-shadow: inset -4px -5px 0 rgba(0,0,0,.18); }
                .cg-result {
                    width: 50px; height: 50px; border-radius: 50%; border: 4px solid #3e2723; cursor: pointer;
                    box-shadow: 0 0 0 4px #FFF59D, inset -5px -6px 0 rgba(0,0,0,.18); animation: mix-bounce .45s ease, cg-glow 1.2s ease-in-out .45s infinite;
                }
                .cg-result-empty { width: 50px; height: 50px; border-radius: 50%; border: 3px dashed #CFD8DC; display: flex; align-items: center; justify-content: center; font-size: 22px; }
                .cg-result-label { text-align: center; font-weight: 900; font-size: 12px; margin: 4px 0 6px; color: #6A1B9A; }
                .cg-lab-pots { display: flex; gap: 4px; justify-content: center; }
                .cg-lab-pot { width: 28px; height: 28px; border-radius: 50%; border: 3px solid; cursor: pointer; padding: 0; flex-shrink: 0; box-shadow: inset -3px -4px 0 rgba(0,0,0,.18); }
                .cg-lab-pot.on { box-shadow: 0 0 0 2px #fff, 0 0 0 4px #3e2723; }
                .cg-recipes { display: flex; gap: 5px; overflow-x: auto; padding: 8px 2px 2px; margin-top: 6px; border-top: 2px dashed #E8EAF6; }
                .cg-recipe {
                    flex-shrink: 0; display: inline-flex; align-items: center; gap: 2px; padding: 3px 8px; border-radius: 14px; border: 2px solid #FFE0B2;
                    background: #FFF8E1; cursor: pointer; font-weight: 900; font-size: 12px; color: #8D6E63; font-family: inherit;
                }
                .cg-recipe:hover { background: #FFE0B2; }
                .cg-dot { width: 14px; height: 14px; border-radius: 50%; border: 1.5px solid rgba(0,0,0,.35); display: inline-block; }

                .cg-overlay { position: absolute; inset: 0; z-index: 50; background: rgba(255,255,255,.92); display: flex; align-items: center; justify-content: center; border-radius: 20px; overflow: hidden; cursor: pointer; }
                .cg-overlay-box { text-align: center; animation: celebrate .5s ease; }
                .cg-confetti { position: absolute; top: -20px; width: 10px; height: 16px; border-radius: 3px; animation: cg-fall linear forwards; }

                /* Tablets / phones: stack, tools become a row under the canvas. */
                @media (max-width: 900px) {
                    .cg-root { --cg-size: min(420px, calc(100vw - 80px)); }
                    .cg-main { grid-template-columns: 1fr; justify-items: center; }
                    .cg-center { order: 1; }
                    .cg-rail { order: 2; flex-direction: row; flex-wrap: wrap; justify-content: center; width: var(--cg-size); }
                    .cg-rail-label, .cg-rail hr { display: none; }
                    .cg-tool, .cg-act { width: 64px; }
                    .cg-panel { order: 3; width: var(--cg-size); max-height: none; overflow: visible; }
                    .cg-tabs { justify-content: flex-start; }
                }

                @keyframes disc-pop { 0% { transform: scale(0) } 60% { transform: scale(1.3) } 100% { transform: scale(1) } }
                @keyframes mix-bounce { 0% { transform: scale(0) rotate(-20deg) } 60% { transform: scale(1.2) } 100% { transform: scale(1) } }
                @keyframes celebrate { 0% { transform: scale(.6) } 60% { transform: scale(1.08) } 100% { transform: scale(1) } }
                @keyframes region-fill { 0% { opacity: .4; transform: scale(.75) } 60% { transform: scale(1.08) } 100% { opacity: 1; transform: scale(1) } }
                @keyframes cg-sparkle { 0% { opacity: 1; transform: translate(-50%, -50%) scale(.4) } 100% { opacity: 0; transform: translate(-50%, -120%) scale(1.6) rotate(40deg) } }
                @keyframes cg-glow { 0%, 100% { box-shadow: 0 0 0 4px #FFF59D, inset -5px -6px 0 rgba(0,0,0,.18) } 50% { box-shadow: 0 0 0 9px #FFF59D, inset -5px -6px 0 rgba(0,0,0,.18) } }
                @keyframes cg-spin { to { transform: rotate(360deg) } }
                @keyframes cg-fall { to { transform: translateY(640px) rotate(720deg) } }
                @keyframes cg-bob { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
            `}</style>

            {/* Picture picker */}
            <div className="cg-top">
                <div className="cg-tabs">
                    {IMAGES.map((img) => (
                        <button key={img.id} type="button" className={`cg-tab ${currentImage === img.id ? 'active' : ''}`} onClick={() => { setCurrentImage(img.id); say(img.label); }}>
                            <span>{img.icon}</span>{img.label}
                            {finished.has(img.id) && <em className="cg-check">✓</em>}
                        </button>
                    ))}
                </div>
            </div>

            <div className="cg-main">
                {/* Tools rail */}
                <div className="cg-rail">
                    <div className="cg-rail-label">TOOLS</div>
                    {[['paint', '🖌️', 'Paint'], ['magic', '🌈', 'Magic'], ['eraser', '🧽', 'Eraser']].map(([id, icon, label]) => (
                        <button key={id} type="button" className={`cg-tool ${tool === id ? 'active' : ''}`} onClick={() => { setTool(id); say(label); }}>
                            <span>{icon}</span>{label}
                        </button>
                    ))}
                    <hr />
                    <button type="button" className="cg-act undo" onClick={undo} disabled={!history.current.some((h) => h.image === currentImage)}><b>↶</b>Undo</button>
                    <button type="button" className="cg-act reset" onClick={startOver} disabled={filledCount === 0}><b>↺</b>Restart</button>
                    <button type="button" className="cg-act save" onClick={savePicture}><b>💾</b>Save</button>
                </div>

                {/* Canvas */}
                <div className="cg-center">
                    <div className="cg-frame">
                        <div ref={canvasRef} className="cg-canvas">
                            <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300">
                                {image.clip && (
                                    <defs>
                                        <clipPath id="rainbow-clip"><rect x={0} y={0} width={300} height={235} /></clipPath>
                                    </defs>
                                )}
                                {image.regions.filter((r) => !r.clip).map(renderRegion)}
                                {image.clip && <g clipPath="url(#rainbow-clip)">{image.regions.filter((r) => r.clip).map(renderRegion)}</g>}
                                {image.decor && <g pointerEvents="none">{image.decor}</g>}
                            </svg>
                            {sparkles.map((s) => <span key={s.id} className="cg-sparkle" style={{ left: s.x, top: s.y }}>✨</span>)}
                        </div>
                    </div>
                    <div className="cg-progress">
                        <span>{pct === 100 ? '🌟' : '🎨'}</span>
                        <div className="cg-track"><div className="cg-fill" style={{ width: `${pct}%` }} /></div>
                        <span>{filledCount} / {total}</span>
                    </div>
                </div>

                {/* Colors + mixing */}
                <div className="cg-panel">
                    <div className="cg-card" style={{ '--edge': '#FFE0B2' }}>
                        <div className="cg-title">
                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span className={`cg-current-dot ${tool === 'magic' ? 'magic' : ''}`} style={activeHex ? { background: activeHex, borderColor: tool === 'eraser' ? '#B0BEC5' : '#333' } : undefined} />
                                {tool === 'magic' ? 'Surprise colors!' : tool === 'eraser' ? 'Eraser' : C[selectedColor].label}
                            </span>
                            <small>🎨 Colors</small>
                        </div>
                        <div className="cg-pots">
                            {BASE.map((b) => pot(b.id, 38, () => pickColor(b.id)))}
                        </div>
                    </div>

                    <div className="cg-card" style={{ '--edge': '#FFF59D' }}>
                        <div className="cg-title">✨ My colors <small>{discovered.length} / {MIX_LIST.length} found</small></div>
                        {discovered.length ? (
                            <div className="cg-mine">
                                {discovered.map((id) => pot(id, 32, () => pickColor(id), newlyFound === id ? 'pop' : ''))}
                            </div>
                        ) : (
                            <div className="cg-empty">Mix two colors below to find new ones!</div>
                        )}
                    </div>

                    <div className="cg-card" style={{ '--edge': '#C5CAE9' }}>
                        <div className="cg-title">🧪 Mix Lab</div>
                        <div className="cg-lab-row">
                            {slot(mixSlot1, () => { if (mixSlot1) { setMixSlot1(mixSlot2); setMixSlot2(null); } })}
                            <span className="cg-op">+</span>
                            {slot(mixSlot2, () => mixSlot2 && setMixSlot2(null))}
                            <span className="cg-op">=</span>
                            {mixResult ? (
                                <button key={mixResult} type="button" className="cg-result" title={`Use ${C[mixResult].label}`} onClick={applyMixedColor} style={{ background: C[mixResult].hex }} />
                            ) : (
                                <span className="cg-result-empty">🪄</span>
                            )}
                        </div>
                        <div className="cg-result-label">
                            {mixResult ? `${C[mixResult].label}! Tap it to paint 👆` : mixSlot1 ? 'Now pick one more color!' : 'Tap 2 colors to mix'}
                        </div>
                        <div className="cg-lab-pots">
                            {BASE.map((b) => (
                                <button
                                    key={b.id}
                                    type="button"
                                    aria-label={`Mix ${b.label}`}
                                    onClick={() => pickMixColor(b.id)}
                                    className={`cg-lab-pot ${mixSlot1 === b.id || mixSlot2 === b.id ? 'on' : ''}`}
                                    style={{ background: b.hex, borderColor: swatchBorder(b.id) }}
                                />
                            ))}
                        </div>
                        <div className="cg-recipes" title="Color recipes - tap one to try it!">
                            {RECIPES.map(([a, b]) => {
                                const r = MIX[`${a}+${b}`];
                                const dot = (id) => <span className="cg-dot" style={{ background: C[id].hex }} />;
                                return (
                                    <button key={`${a}${b}`} type="button" className="cg-recipe" title={`${C[a].label} + ${C[b].label}`} onClick={() => { setMixSlot1(a); setMixSlot2(b); playTone(r); }}>
                                        {dot(a)}+{dot(b)}={discovered.includes(r) ? dot(r) : <span>❓</span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {celebrating && (
                <div className="cg-overlay" onClick={() => setCelebrating(false)}>
                    {range(36).map((i) => (
                        <span
                            key={i}
                            className="cg-confetti"
                            style={{
                                left: `${(i * 37) % 100}%`,
                                background: CONFETTI[i % CONFETTI.length],
                                animationDuration: `${1.6 + (i % 5) * 0.3}s`,
                                animationDelay: `${(i % 7) * 0.08}s`,
                            }}
                        />
                    ))}
                    <div className="cg-overlay-box">
                        <div style={{ fontSize: 84, animation: 'cg-bob 1s ease-in-out infinite' }}>{image.icon}</div>
                        <div style={{ fontSize: 40, fontWeight: 900 }}>Beautiful! 🎉</div>
                        <div style={{ fontSize: 20, color: '#16A34A', fontWeight: 800 }}>{phrase}</div>
                        <div style={{ fontSize: 34, marginTop: 6 }}>⭐⭐⭐</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColoringGame;
